import { AttachmentPreviewButton } from '@/shared/components/documents/AttachmentPreviewButton';
import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  Star,
  FileText,
  Truck,
  CreditCard,
  FlaskConical,
  MessageSquare,
  Wallet,
  FolderOpen,
  ShieldAlert,
  Receipt,
  ClipboardList,
  TrendingUp,
  TrendingDown,
  Clock,
  Package,
  AlertTriangle,
  CheckCircle2,
  IndianRupee,
  LayoutGrid,
  Filter,
  Target,
  Briefcase,
  Edit,
  UserPlus,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { ROUTES } from '@/routes/paths';
import { useHasPermission } from '@/shared/permissions/useHasPermission';
import { useClient360 } from '../api';
import type { Client360Response } from '../types';
import { SampleDetailDrawer } from '@/features/sampling/components/SampleDetailDrawer';

// ── Tab definitions (no duplicate icons) ─────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid },
  { id: 'orders', label: 'Orders & Invoices', icon: FileText },
  { id: 'deliveries', label: 'Deliveries', icon: Truck },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'samples', label: 'Samples & Complaints', icon: FlaskConical },
  { id: 'communications', label: 'Communications', icon: MessageSquare },
  { id: 'financials', label: 'Financials', icon: Wallet },
  { id: 'documents', label: 'Documents', icon: FolderOpen },
] as const;

type TabId = (typeof TABS)[number]['id'];

const CHANNEL_LABELS: Record<string, string> = {
  DirectCustomer: 'Direct',
  CustomerFromChannel: 'Channel',
  Reseller: 'Reseller',
  Wholesaler: 'Wholesaler',
  Dealer: 'Dealer',
  Distributor: 'Distributor',
  Stockist: 'Stockist',
  NationalDistributor: 'Nat. Distributor',
};

function creditStatusColor(outstanding: number, creditLimit: number): { bg: string; text: string; label: string } {
  if (creditLimit <= 0) return { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600', label: 'No Limit' };
  const ratio = outstanding / creditLimit;
  if (ratio >= 0.9) return { bg: 'bg-red-50 dark:bg-red-950', text: 'text-red-600', label: 'Critical' };
  if (ratio >= 0.6) return { bg: 'bg-amber-50 dark:bg-amber-950', text: 'text-amber-600', label: 'Warning' };
  return { bg: 'bg-green-50 dark:bg-green-950', text: 'text-green-600', label: 'Healthy' };
}

function formatCurrency(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
  return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function formatMinor(minor: number) {
  return formatCurrency(minor / 100);
}

function formatDate(dateStr?: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatRelativeDate(dateStr?: string | null) {
  if (!dateStr) return '—';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function withQuery(path: string, params: Record<string, string | number | null | undefined>) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    query.set(key, String(value));
  });
  const qs = query.toString();
  return qs ? `${path}?${qs}` : path;
}

// ── Main Component ───────────────────────────────────────────────────────────

export function ClientProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [viewSampleId, setViewSampleId] = useState<string | null>(null);
  const canEditContact = useHasPermission('contacts.update');
  const canCreateContact = useHasPermission('contacts.insert');

  const { data, isLoading, isError } = useClient360(id || null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <ShieldAlert className="w-8 h-8 text-destructive" />
        </div>
        <div className="text-center">
          <p className="font-medium text-foreground">Failed to load client profile</p>
          <p className="text-sm text-muted-foreground mt-1">The client may not exist or you don't have access.</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/clients')}>
          Back to Clients
        </Button>
      </div>
    );
  }

  const { profile, channelInfo, financials, loyalty } = data;
  const creditStatus = creditStatusColor(financials.outstanding, financials.creditLimit);

  // Both buttons used to navigate with no context at all — the customer
  // whose page you're already on had to be re-searched from scratch on the
  // next screen. Quotation reuses the document editor's proven ?name=/
  // ?phone=/?address=/?gstin= prefill (the same one Deal detail's "Create
  // Quotation" already relies on); Order carries the real customer id
  // through to CreateSalesOrderPage's matching prefill (added alongside
  // this fix, since that page previously read no query params at all).
  const clientAddressLine = [profile.address?.street, profile.address?.city, profile.address?.state]
    .filter(Boolean)
    .join(', ');
  const clientContext = {
    name: profile.name,
    phone: profile.phone || '',
    email: profile.email || '',
    address: clientAddressLine,
    gstin: profile.gstNumber || '',
    clientId: profile.entityIds.clientId || profile.entityIds.customerId || profile.id,
    contactId: profile.entityIds.contactId || '',
  };
  const quotationHref = withQuery(ROUTES.documentEditor, { type: 'quotation', ...clientContext });
  const orderHref = profile.entityIds.customerId
    ? withQuery(ROUTES.salesOrderCreate, {
        clientId: profile.entityIds.customerId,
        clientName: profile.name,
        clientPhone: profile.phone || '',
        clientAddress: clientAddressLine,
      })
    : ROUTES.salesOrderCreate;
  // Unlike Quotation/Order above, this had nothing prefilled at all — the
  // salesperson had to re-find the client's invoice by hand every time.
  const paymentHref = withQuery(ROUTES.accountingV2Vouchers, { customerName: profile.name });
  const accountingPartyHref = profile.entityIds.accPartyId
    ? `/accounting-v2/parties/${profile.entityIds.accPartyId}`
    : withQuery(ROUTES.accountingV2Parties, { search: profile.name });
  // No CRM Contact linked yet — hand off to the same "New Contact" form the
  // Contacts module uses, prefilled from what's already known here, instead
  // of leaving no way at all to create/link one from this screen.
  const addContactHref = withQuery(ROUTES.contactNew, {
    name: profile.name,
    company: profile.company,
    phone: profile.phone || '',
    email: profile.email || '',
    address: profile.address?.street || '',
    city: profile.address?.city || '',
    state: profile.address?.state || '',
    pin: profile.address?.zipCode || '',
    gstin: profile.gstNumber || '',
  });
  const sampleHref = withQuery(ROUTES.samplingNew, {
    clientName: profile.name,
    phone: profile.phone || '',
    email: profile.email || '',
    address: clientAddressLine,
  });
  const complaintHref = withQuery(ROUTES.serviceDesk, { clientName: profile.name, phone: profile.phone || '', email: profile.email || '' });

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header — compact, information-rich */}
      <header className="px-6 py-4 border-b border-border bg-card shadow-sm">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/clients')} aria-label="Back to client list" className="mt-0.5">
            <ArrowLeft className="w-4 h-4" />
          </Button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-semibold truncate leading-tight">{profile.name}</h1>
                {profile.company && profile.company !== profile.name && (
                  <p className="text-sm text-muted-foreground truncate">{profile.company}</p>
                )}
              </div>
              <Badge variant="outline" className="shrink-0">{CHANNEL_LABELS[channelInfo.channelCategory] || channelInfo.channelCategory}</Badge>
              <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${creditStatus.bg} ${creditStatus.text}`}>
                {creditStatus.label}
              </div>
              {loyalty.balance > 0 && (
                <Badge variant="secondary" className="gap-1 shrink-0">
                  <Star className="w-3 h-3 text-amber-500" /> {loyalty.balance.toLocaleString()} pts
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-5 mt-2 text-sm text-muted-foreground flex-wrap">
              {profile.email && (
                <a href={`mailto:${profile.email}`} className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                  <Mail className="w-3.5 h-3.5" /> {profile.email}
                </a>
              )}
              {profile.phone && (
                <a href={`tel:${profile.phone}`} className="flex items-center gap-1.5 hover:text-foreground transition-colors">
                  <Phone className="w-3.5 h-3.5" /> {profile.phone}
                </a>
              )}
              {profile.address?.city && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" /> {profile.address.city}{profile.address.state ? `, ${profile.address.state}` : ''}
                </span>
              )}
              {profile.gstNumber && (
                <span className="flex items-center gap-1.5 font-mono text-xs">
                  GST: {profile.gstNumber}
                </span>
              )}
            </div>
          </div>

          {/* Header stats — compact KPIs */}
          <div className="hidden xl:flex items-center gap-4 shrink-0">
            <div className="text-center px-3">
              <p className="text-xs text-muted-foreground">Revenue</p>
              <p className="text-sm font-semibold">{formatCurrency(financials.lifetimeRevenue)}</p>
            </div>
            <div className="w-px h-8 bg-border" />
            <div className="text-center px-3">
              <p className="text-xs text-muted-foreground">Outstanding</p>
              <p className={`text-sm font-semibold ${financials.outstanding > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                {formatCurrency(financials.outstanding)}
              </p>
            </div>
            <div className="w-px h-8 bg-border" />
            <div className="text-center px-3">
              <p className="text-xs text-muted-foreground">Orders</p>
              <p className="text-sm font-semibold">{financials.lifetimeOrders}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Body: Tabs + Content + Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Main content area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tab bar */}
          <nav className="flex border-b border-border bg-card px-4 overflow-x-auto shrink-0" aria-label="Client tabs">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary text-primary bg-primary/5'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  aria-selected={activeTab === tab.id}
                  role="tab"
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Tab content */}
          <div className="flex-1 overflow-auto p-6">
            {activeTab === 'overview' && <OverviewTab data={data} />}
            {activeTab === 'orders' && <OrdersInvoicesTab data={data} />}
            {activeTab === 'deliveries' && <DeliveriesTab data={data} />}
            {activeTab === 'payments' && <PaymentsTab data={data} />}
            {activeTab === 'samples' && <SamplesComplaintsTab data={data} onViewSample={setViewSampleId} />}
            {activeTab === 'communications' && <CommunicationsTab data={data} />}
            {activeTab === 'financials' && <FinancialsTab data={data} />}
            {activeTab === 'documents' && <DocumentsTab data={data} />}
          </div>
        </div>

        {/* Sidebar — Quick Actions */}
        <aside className="w-60 border-l border-border bg-card p-4 flex flex-col gap-3 shrink-0 hidden lg:flex">
          <div className={`rounded-xl border p-3 ${financials.outstanding > 0 ? 'border-amber-200 bg-amber-50/80 dark:border-amber-900 dark:bg-amber-950/30' : 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900 dark:bg-emerald-950/20'}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-data-xs font-semibold uppercase tracking-wider text-muted-foreground">Pending Balance</p>
                <p className={`mt-1 text-lg font-bold ${financials.outstanding > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
                  {formatCurrency(financials.outstanding)}
                </p>
              </div>
              {financials.outstanding > 0 ? <AlertTriangle className="mt-1 h-4 w-4 text-amber-600" /> : <CheckCircle2 className="mt-1 h-4 w-4 text-emerald-600" />}
            </div>
            <div className="mt-2 space-y-1 text-data-xs text-muted-foreground">
              <div className="flex justify-between gap-2">
                <span>Pending payments</span>
                <span className="font-semibold text-foreground">{financials.pendingPayments}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span>Credit limit</span>
                <span className="font-semibold text-foreground">{financials.creditLimit ? formatCurrency(financials.creditLimit) : 'Not set'}</span>
              </div>
              {!!financials.credit?.overdueAmount && (
                <div className="flex justify-between gap-2">
                  <span>Overdue</span>
                  <span className="font-semibold text-red-600">{formatCurrency(financials.credit.overdueAmount)}</span>
                </div>
              )}
              {!!financials.credit?.oldDues && (
                <div className="flex justify-between gap-2">
                  <span>Old dues (before CRM)</span>
                  <span className="font-semibold text-foreground">{formatCurrency(financials.credit.oldDues)}</span>
                </div>
              )}
            </div>
            {financials.credit?.needsApproval && (
              <p className="mt-2 rounded-lg bg-amber-50 px-2 py-1.5 text-data-xs font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                New bills go on credit — posting needs Admin / Super Admin approval.
              </p>
            )}
            <Button size="sm" variant="outline" className="mt-3 w-full justify-center" onClick={() => navigate(accountingPartyHref)}>
              Adjust Credit
            </Button>
          </div>

          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Quick Actions</h3>
          <QuickActionButton icon={FileText} label="Create Quotation" onClick={() => navigate(quotationHref)} />
          <QuickActionButton icon={ClipboardList} label="Create Order" onClick={() => navigate(orderHref)} />
          <QuickActionButton icon={CreditCard} label="Record Payment" onClick={() => navigate(paymentHref)} />
          <QuickActionButton icon={FlaskConical} label="Sample Request" onClick={() => navigate(sampleHref)} />
          <QuickActionButton icon={ShieldAlert} label="Log Complaint" onClick={() => navigate(complaintHref)} />
          <QuickActionButton icon={Receipt} label="Adjust Credit" onClick={() => navigate(accountingPartyHref)} />

          {/* Entity links — always shown: previously this whole block (and
              with it, the only way to reach or create a CRM contact) simply
              vanished when a client had neither a contact nor an accounting
              party linked yet. */}
          <div className="mt-4 pt-4 border-t border-border">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Linked Entities</h3>
            <div className="space-y-1.5 text-xs">
              {profile.entityIds.contactId ? (
                <>
                  <button onClick={() => navigate(ROUTES.contactDetail.replace(':id', profile.entityIds.contactId!))} className="text-primary hover:underline flex items-center gap-1">
                    View CRM Contact
                  </button>
                  {canEditContact && (
                    <button onClick={() => navigate(ROUTES.contactEdit.replace(':id', profile.entityIds.contactId!))} className="text-primary hover:underline flex items-center gap-1">
                      <Edit className="w-3 h-3" /> Edit CRM Contact
                    </button>
                  )}
                </>
              ) : (
                canCreateContact && (
                  <button onClick={() => navigate(addContactHref)} className="text-primary hover:underline flex items-center gap-1">
                    <UserPlus className="w-3 h-3" /> Add CRM Contact
                  </button>
                )
              )}
              {profile.entityIds.accPartyId ? (
                <button onClick={() => navigate(accountingPartyHref)} className="text-primary hover:underline block">
                  View in Accounting
                </button>
              ) : (
                <button onClick={() => navigate(accountingPartyHref)} className="text-primary hover:underline block">
                  Find Accounting Party
                </button>
              )}
            </div>
          </div>
        </aside>
      </div>
      <SampleDetailDrawer sampleId={viewSampleId} open={Boolean(viewSampleId)} onClose={() => setViewSampleId(null)} />
    </div>
  );
}

// ── Reusable Components ──────────────────────────────────────────────────────

function QuickActionButton({ icon: Icon, label, onClick }: { icon: React.ComponentType<{ className?: string }>; label: string; onClick: () => void }) {
  return (
    <Button variant="ghost" size="sm" className="justify-start gap-2.5 w-full h-9 px-3 hover:bg-primary/5 hover:text-primary" onClick={onClick}>
      <Icon className="w-4 h-4 shrink-0" /> <span className="truncate">{label}</span>
    </Button>
  );
}

function MetricCard({ label, value, sub, icon: Icon, color = 'primary' }: {
  label: string;
  value: string;
  sub?: string;
  icon?: React.ComponentType<{ className?: string }>;
  color?: 'primary' | 'green' | 'amber' | 'red' | 'purple';
}) {
  const colorMap = {
    primary: 'bg-primary/10 text-primary',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600',
  };
  return (
    <div className="rounded-xl border border-border bg-card p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
        {Icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorMap[color]}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
      <p className="text-2xl font-bold mt-2 tracking-tight">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function SectionHeader({ title, count, action }: { title: string; count?: number; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {count !== undefined && count > 0 && (
          <Badge variant="secondary" className="text-xs">{count}</Badge>
        )}
      </div>
      {action}
    </div>
  );
}

function DataTable({ headers, children, empty }: { headers: string[]; children: React.ReactNode; empty?: string }) {
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr className="text-left">
            {headers.map((h, i) => (
              <th key={i} className={`px-4 py-2.5 font-medium text-muted-foreground text-xs uppercase tracking-wider ${h.startsWith('_right:') ? 'text-right' : ''}`}>
                {h.replace('_right:', '')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {children}
        </tbody>
      </table>
      {empty && (
        <div className="py-8 text-center text-sm text-muted-foreground">{empty}</div>
      )}
    </div>
  );
}

// ── Tab Components ───────────────────────────────────────────────────────────

function OverviewTab({ data }: { data: Client360Response }) {
  const { financials, activity, loyalty, documents, pipeline, followupSummary } = data;
  const lastOrderDate = documents.orders[0]?.date || documents.orders[0]?.createdAt;
  const daysSinceLastOrder = lastOrderDate
    ? Math.floor((Date.now() - new Date(lastOrderDate).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const openOrders = documents.orders.filter(o => !['closed', 'cancelled', 'invoiced'].includes(o.status || '')).length;

  return (
    <div className="space-y-8">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <MetricCard label="Lifetime Revenue" value={formatCurrency(financials.lifetimeRevenue)} icon={TrendingUp} color="green" sub={`${financials.lifetimeOrders} orders total`} />
        <MetricCard label="Open Orders" value={String(openOrders)} icon={Package} color="primary" sub={openOrders > 0 ? 'Pending fulfillment' : 'All fulfilled'} />
        <MetricCard label="Outstanding AR" value={formatCurrency(financials.outstanding)} icon={financials.outstanding > 0 ? AlertTriangle : CheckCircle2} color={financials.outstanding > 0 ? 'amber' : 'green'} sub={financials.creditLimit > 0 ? `Limit: ${formatCurrency(financials.creditLimit)}` : undefined} />
        <MetricCard label="Last Order" value={daysSinceLastOrder !== null ? `${daysSinceLastOrder}d` : '—'} icon={Clock} color={daysSinceLastOrder !== null && daysSinceLastOrder > 60 ? 'red' : 'primary'} sub={lastOrderDate ? formatDate(lastOrderDate) : 'No orders yet'} />
        <MetricCard label="Loyalty Points" value={loyalty.balance.toLocaleString()} icon={Star} color="purple" sub={loyalty.totalEarned > 0 ? `${loyalty.totalEarned.toLocaleString()} earned` : 'No program'} />
      </div>

      {/* Pipeline & follow-up summary — same numbers the Account 360 panel on the Contact drawer shows */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <MetricCard label="Avg Order Value" value={formatCurrency(financials.averageOrderValue)} icon={IndianRupee} color="primary" />
        <MetricCard label="Pending Payments" value={String(financials.pendingPayments)} icon={financials.pendingPayments > 0 ? AlertTriangle : CheckCircle2} color={financials.pendingPayments > 0 ? 'amber' : 'green'} />
        <MetricCard label="Quotations" value={String(pipeline.quotations)} icon={FileText} color="primary" sub={`${pipeline.enquiries} enquiries`} />
        <MetricCard label="Won / Lost Deals" value={`${pipeline.won} / ${pipeline.lost}`} icon={pipeline.won >= pipeline.lost ? TrendingUp : TrendingDown} color={pipeline.won >= pipeline.lost ? 'green' : 'red'} />
        <MetricCard label="Next Follow-up" value={followupSummary.next ? formatDate(followupSummary.next) : '—'} icon={Clock} color="purple" sub={followupSummary.last ? `Last: ${formatDate(followupSummary.last)}` : 'No follow-ups yet'} />
      </div>

      {/* Two-column layout for recent activity and quick stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity Timeline */}
        <div className="rounded-xl border border-border bg-card p-5">
          <SectionHeader title="Recent Activity" />
          <div className="space-y-3 max-h-72 overflow-auto">
            {activity.leads?.slice(0, 3).map((lead) => (
              <TimelineItem key={lead._id} date={lead.created_at} color="border-cyan-400" badge={lead.status}>
                Lead: {lead.name} {lead.niche ? `(${lead.niche})` : ''}
              </TimelineItem>
            ))}
            {activity.deals.slice(0, 4).map((deal) => (
              <TimelineItem key={deal._id} date={deal.created_at} color="border-blue-400" badge={deal.stage}>
                Deal: {deal.title} {deal.value ? `(${formatCurrency(deal.value)})` : ''}
              </TimelineItem>
            ))}
            {documents.invoices.slice(0, 4).map((inv) => (
              <TimelineItem key={inv._id} date={inv.date || inv.createdAt} color="border-green-400" badge={inv.status}>
                Invoice {inv.invoiceNo || inv.invoiceNumber || '—'} — {inv.grandTotalMinor ? formatMinor(inv.grandTotalMinor) : inv.grandTotal ? formatCurrency(inv.grandTotal) : ''}
              </TimelineItem>
            ))}
            {documents.samples.slice(0, 3).map((s) => (
              <TimelineItem key={s._id} date={s.createdAt} color="border-purple-400" badge={s.status}>
                Sample {s.code}
              </TimelineItem>
            ))}
            {documents.complaints.slice(0, 2).map((c) => (
              <TimelineItem key={c._id} date={c.createdAt} color="border-red-400" badge={c.cmsStatus || c.status}>
                Complaint: {c.title || c.ticketNumber}
              </TimelineItem>
            ))}
            {(activity.leads?.length || 0) === 0 && activity.deals.length === 0 && documents.invoices.length === 0 && documents.samples.length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">No recent activity.</p>
            )}
          </div>
        </div>

        {/* Document Summary Cards */}
        <div className="rounded-xl border border-border bg-card p-5">
          <SectionHeader title="Document Summary" />
          <div className="grid grid-cols-2 gap-3">
            <MiniStat label="Leads" count={activity.leads?.length || 0} icon={Target} />
            <MiniStat label="Deals" count={activity.deals.length} icon={Briefcase} />
            <MiniStat label="Quotations" count={documents.quotations.length} icon={FileText} />
            <MiniStat label="Orders" count={documents.orders.length} icon={ClipboardList} />
            <MiniStat label="Invoices" count={documents.invoices.length} icon={Receipt} />
            <MiniStat label="Deliveries" count={documents.deliveries.length} icon={Truck} />
            <MiniStat label="Payments" count={documents.payments.length} icon={CreditCard} />
            <MiniStat label="Samples" count={documents.samples.length} icon={FlaskConical} />
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelineItem({ date, color, badge, children }: { date?: string | null; color: string; badge?: string | null; children: React.ReactNode }) {
  return (
    <div className={`flex items-start gap-3 border-l-2 ${color} pl-3 py-1.5`}>
      <span className="text-xs text-muted-foreground whitespace-nowrap mt-0.5 w-16 shrink-0">{formatRelativeDate(date)}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">{children}</p>
      </div>
      {badge && <Badge variant="outline" className="text-xs shrink-0">{badge}</Badge>}
    </div>
  );
}

function MiniStat({ label, count, icon: Icon }: { label: string; count: number; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border/50 p-3 bg-muted/20">
      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
      </div>
      <span className="text-sm font-semibold">{count}</span>
    </div>
  );
}

// Client 360 must only point to a route that renders that exact document.
// An order and quotation share a database model, but not a lifecycle.
function docDetailPath(docType: string, id: string, source?: string): string | null {
  if (source !== 'sales-order') return null;
  if (docType === 'Quotation') return ROUTES.quotationDetail.replace(':id', id);
  if (docType === 'Proforma') return ROUTES.proformaDetail.replace(':id', id);
  if (docType === 'Invoice') return ROUTES.invoiceDetail.replace(':id', id);
  if (docType === 'Order') return ROUTES.salesOrderDetail.replace(':id', id);
  return null;
}

function documentPreviewUrl(docType: string, id: string, source?: string): string | null {
  if (docType === 'Quotation') return `/api/documents/rbm-quotation/${id}`;
  if (docType === 'Proforma') return `/api/documents/rbm-proforma/${id}`;
  if (docType === 'Invoice' && source === 'accounts-invoice') return `/api/documents/rbm-invoice/${id}`;
  if (docType === 'Delivery' && source === 'commercial') return `/api/documents/challan/${id}`;
  return null;
}

function ViewDocumentButton({ docType, id, source }: { docType: string; id: string; source?: string }) {
  const navigate = useNavigate();
  const route = docDetailPath(docType, id, source);
  const preview = documentPreviewUrl(docType, id, source);
  if (route) return <Button size="sm" variant="outline" onClick={() => navigate(route)}>View</Button>;
  if (preview) return <a href={preview} target="_blank" rel="noreferrer" className="inline-flex h-8 items-center rounded border border-outline px-3 text-sm hover:bg-surface-variant">View</a>;
  return <span className="text-xs text-muted-foreground">—</span>;
}

function OrdersInvoicesTab({ data }: { data: Client360Response }) {
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const allDocs = useMemo(() => {
    const docs = [
      ...data.documents.quotations.map(d => ({ ...d, docType: 'Quotation' as const, no: d.quotationNo || d.documentNumber, total: d.grandTotalMinor ? d.grandTotalMinor / 100 : d.grandTotal || 0 })),
      ...data.documents.proformas.map(d => ({ ...d, docType: 'Proforma' as const, no: d.orderNumber || d.documentNumber, total: d.grandTotalMinor ? d.grandTotalMinor / 100 : d.grandTotal || 0 })),
      ...data.documents.orders.map(d => ({ ...d, docType: 'Order' as const, no: d.orderNo || d.documentNumber, total: d.grandTotalMinor ? d.grandTotalMinor / 100 : d.grandTotal || 0 })),
      ...data.documents.invoices.map(d => ({ ...d, docType: 'Invoice' as const, no: d.invoiceNo || d.invoiceNumber, total: d.grandTotalMinor ? d.grandTotalMinor / 100 : d.grandTotal || 0 })),
      ...data.documents.creditNotes.map(d => ({ ...d, docType: 'Credit Note' as const, no: d.creditNoteNo, total: d.grandTotalMinor ? d.grandTotalMinor / 100 : 0 })),
    ].sort((a, b) => new Date(b.date || b.createdAt || 0).getTime() - new Date(a.date || a.createdAt || 0).getTime());
    const byType = typeFilter === 'all' ? docs : docs.filter(d => d.docType === typeFilter);
    const q = search.trim().toLowerCase();
    if (!q) return byType;
    return byType.filter((d) => [
      d.docType,
      d.no,
      d.customerName,
      d.status,
    ].some((value) => String(value || '').toLowerCase().includes(q)));
  }, [data, search, typeFilter]);
  const totalPages = Math.max(1, Math.ceil(allDocs.length / pageSize));
  const pageDocs = allDocs.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter]);

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          {['all', 'Quotation', 'Proforma', 'Order', 'Invoice', 'Credit Note'].map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                typeFilter === t ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'all' ? 'All' : t}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, number, status..."
          className="h-8 w-full rounded-md border border-border bg-background px-3 text-xs sm:w-64"
        />
      </div>

      {allDocs.length === 0 ? (
        <EmptyState label="No documents match this filter." icon={FileText} />
      ) : (
        <DataTable headers={['Type', 'Number', 'Date', 'Status', '_right:Amount', 'View']}>
          {pageDocs.map((doc) => {
            return (
              <tr
                key={doc._id}
                className="hover:bg-muted/30 transition-colors"
              >
                <td className="px-4 py-2.5"><Badge variant="outline" className="text-xs">{doc.docType}</Badge></td>
                <td className="px-4 py-2.5 font-mono text-xs">{doc.no || '—'}</td>
                <td className="px-4 py-2.5 text-muted-foreground text-xs">{formatDate(doc.date || doc.createdAt)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={doc.status} /></td>
                <td className="px-4 py-2.5 text-right font-mono text-sm font-medium">{doc.total ? formatCurrency(doc.total) : '—'}</td>
                <td className="px-4 py-2.5"><ViewDocumentButton docType={doc.docType} id={doc._id} source={doc.client360Source} /></td>
              </tr>
            );
          })}
        </DataTable>
      )}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
          <span>Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, allDocs.length)} of {allDocs.length} · Newest first</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DeliveriesTab({ data }: { data: Client360Response }) {
  const deliveries = data.documents.deliveries;
  if (deliveries.length === 0) return <EmptyState label="No deliveries recorded." icon={Truck} />;

  return (
    <DataTable headers={['Number', 'Date', 'Status', 'Carrier', 'Tracking', 'View']}>
      {deliveries.map((d) => (
        <tr key={d._id} className="hover:bg-muted/30 transition-colors">
          <td className="px-4 py-2.5 font-mono text-xs font-medium">{d.deliveryNo || d.documentNumber || '—'}</td>
          <td className="px-4 py-2.5 text-muted-foreground text-xs">{formatDate(d.date || d.createdAt)}</td>
          <td className="px-4 py-2.5"><StatusBadge status={d.status} /></td>
          <td className="px-4 py-2.5 text-sm">{d.carrier || '—'}</td>
          <td className="px-4 py-2.5 font-mono text-xs">{d.trackingNumber || '—'}</td>
          <td className="px-4 py-2.5"><ViewDocumentButton docType="Delivery" id={d._id} source={d.client360Source} /></td>
        </tr>
      ))}
    </DataTable>
  );
}

function PaymentsTab({ data }: { data: Client360Response }) {
  const { financials, documents } = data;
  const payments = documents.payments;
  const totalAging = financials.aging.current + financials.aging['1-30'] + financials.aging['31-60'] + financials.aging['61-90'] + financials.aging['90+'];

  const credit = financials.credit;
  const advances = documents.advances || [];
  const clearances = documents.creditClearances || [];

  return (
    <div className="space-y-6">
      {credit && <CreditStatusCard credit={credit} />}

      <section>
        <SectionHeader title="Advance & payment records" count={advances.length} />
        <p className="mb-3 text-xs text-muted-foreground">Payments recorded against this client's orders (advances, and amounts collected at dispatch). Accounts receipts are in Payment History below.</p>
        {advances.length === 0 ? (
          <EmptyState label="No advance or order payments recorded." icon={Wallet} />
        ) : (
          <DataTable headers={['Date', 'Order', 'Method', 'Reference', 'Recorded by', '_right:Amount']}>
            {advances.map((a, i) => (
              <tr key={`${a.orderId}-${i}`} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5 text-muted-foreground text-xs">{formatDate(a.date)}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{a.orderNumber || '—'}<span className="ml-1 font-sans text-muted-foreground">{a.documentType === 'sales_order' ? '' : `(${a.documentType})`}</span></td>
                <td className="px-4 py-2.5"><Badge variant="secondary" className="text-xs capitalize">{a.method || '—'}</Badge></td>
                <td className="px-4 py-2.5 text-xs">{a.reference || '—'}{a.hasProof ? <span className="ml-1 text-emerald-600">· proof attached</span> : null}</td>
                <td className="px-4 py-2.5 text-xs">{a.recordedByName || '—'}</td>
                <td className="px-4 py-2.5 text-right font-mono text-sm font-medium text-green-600">{formatCurrency(a.amount)}</td>
              </tr>
            ))}
          </DataTable>
        )}
      </section>

      <section>
        <SectionHeader title="Credit clearances" count={clearances.length} />
        <p className="mb-3 text-xs text-muted-foreground">Bills that needed credit approval (over the limit or overdue) and who approved or rejected them, and orders cleared to dispatch on credit.</p>
        {clearances.length === 0 ? (
          <EmptyState label="No credit approvals or clearances." icon={CheckCircle2} />
        ) : (
          <DataTable headers={['Date', 'Document', 'Why', 'Outcome', 'By', '_right:Amount']}>
            {clearances.map((c) => (
              <tr key={`${c.kind}-${c.id}`} className="align-top hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5 text-muted-foreground text-xs">{formatDate(c.at)}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{c.number}{c.invoiceNumber ? <div className="text-muted-foreground">{c.invoiceNumber}</div> : null}<div className="font-sans text-muted-foreground">{c.kind === 'invoice' ? 'Invoice request' : 'Dispatch'}</div></td>
                <td className="max-w-xs px-4 py-2.5 text-xs">{c.reason || '—'}{c.note ? <div className="mt-0.5 text-muted-foreground">Note: {c.note}</div> : null}</td>
                <td className="px-4 py-2.5">
                  <Badge variant="secondary" className={`text-xs ${c.outcome === 'approved' ? 'bg-emerald-50 text-emerald-700' : c.outcome === 'rejected' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                    {c.outcome === 'approved' ? 'Approved' : c.outcome === 'rejected' ? 'Rejected' : c.outcome === 'pending' ? 'Waiting' : c.outcome}
                  </Badge>
                </td>
                <td className="px-4 py-2.5 text-xs">{c.byName || (c.outcome === 'pending' ? '—' : '')}{c.requestedByName ? <div className="text-muted-foreground">Raised by {c.requestedByName}</div> : null}</td>
                <td className="px-4 py-2.5 text-right font-mono text-sm">{formatCurrency(c.amount)}</td>
              </tr>
            ))}
          </DataTable>
        )}
      </section>

      {(documents.salesHandoffs || []).length > 0 && (
        <section>
          <SectionHeader title="Order credit & payment arrangements" count={documents.salesHandoffs!.length} />
          <p className="mb-3 text-xs text-muted-foreground">Amounts recorded by Sales at hand-off. Later receipts appear in Payment History.</p>
          <DataTable headers={['Order', 'Payment arrangement', '_right:Credit amount', '_right:Advance received', '_right:Balance at hand-off']}>
            {documents.salesHandoffs!.map((handoff) => (
              <tr key={handoff.orderId}>
                <td className="px-4 py-2.5 font-mono text-xs">{handoff.orderNumber}</td>
                <td className="px-4 py-2.5"><Badge variant="secondary">{handoff.paymentArrangement}</Badge></td>
                <td className="px-4 py-2.5 text-right">{handoff.paymentArrangement.toLowerCase() === 'credit' ? (handoff.creditAmount === null ? 'Not recorded' : formatCurrency(handoff.creditAmount)) : '—'}</td>
                <td className="px-4 py-2.5 text-right">{formatCurrency(handoff.amountReceived)}</td>
                <td className="px-4 py-2.5 text-right">{formatCurrency(handoff.balanceAmount)}</td>
              </tr>
            ))}
          </DataTable>
        </section>
      )}
      {/* Aging breakdown — visual bar */}
      <div className="rounded-xl border border-border bg-card p-5">
        <SectionHeader title="Aging Breakdown" />
        {totalAging > 0 ? (
          <>
            <div className="flex h-3 rounded-full overflow-hidden mb-4">
              {financials.aging.current > 0 && <div className="bg-green-500" style={{ width: `${(financials.aging.current / totalAging) * 100}%` }} title="Current" />}
              {financials.aging['1-30'] > 0 && <div className="bg-blue-500" style={{ width: `${(financials.aging['1-30'] / totalAging) * 100}%` }} title="1-30 days" />}
              {financials.aging['31-60'] > 0 && <div className="bg-amber-500" style={{ width: `${(financials.aging['31-60'] / totalAging) * 100}%` }} title="31-60 days" />}
              {financials.aging['61-90'] > 0 && <div className="bg-orange-500" style={{ width: `${(financials.aging['61-90'] / totalAging) * 100}%` }} title="61-90 days" />}
              {financials.aging['90+'] > 0 && <div className="bg-red-500" style={{ width: `${(financials.aging['90+'] / totalAging) * 100}%` }} title="90+ days" />}
            </div>
            <div className="grid grid-cols-5 gap-3">
              <AgingCell label="Current" value={financials.aging.current} color="bg-green-500" />
              <AgingCell label="1-30 Days" value={financials.aging['1-30']} color="bg-blue-500" />
              <AgingCell label="31-60 Days" value={financials.aging['31-60']} color="bg-amber-500" />
              <AgingCell label="61-90 Days" value={financials.aging['61-90']} color="bg-orange-500" />
              <AgingCell label="90+ Days" value={financials.aging['90+']} color="bg-red-500" />
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No outstanding aging.</p>
        )}
      </div>

      {/* Payment History */}
      <div>
        <SectionHeader title="Payment History" count={payments.length} />
        {payments.length === 0 ? (
          <EmptyState label="No payments recorded." icon={CreditCard} />
        ) : (
          <DataTable headers={['Number', 'Date', 'Method', 'Reference', '_right:Amount']}>
            {payments.map((p) => (
              <tr key={p._id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5 font-mono text-xs font-medium">{p.paymentNumber || '—'}</td>
                <td className="px-4 py-2.5 text-muted-foreground text-xs">{formatDate(p.date || p.createdAt)}</td>
                <td className="px-4 py-2.5"><Badge variant="secondary" className="text-xs capitalize">{p.paymentMethod?.replace('_', ' ') || '—'}</Badge></td>
                <td className="px-4 py-2.5 font-mono text-xs">{p.referenceNumber || '—'}</td>
                <td className="px-4 py-2.5 text-right font-mono text-sm font-medium text-green-600">{p.amount ? formatCurrency(p.amount) : '—'}</td>
              </tr>
            ))}
          </DataTable>
        )}
      </div>
    </div>
  );
}

/** Where the client stands on credit — the numbers the invoice credit approval uses. */
function CreditStatusCard({ credit }: { credit: NonNullable<Client360Response['financials']['credit']> }) {
  const cell = (label: string, value: string, tone = '') => (
    <div>
      <p className="text-data-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-0.5 text-sm font-semibold ${tone}`}>{value}</p>
    </div>
  );
  return (
    <div className={`rounded-xl border p-5 ${credit.needsApproval ? 'border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/20' : 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20'}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Credit status</h3>
        <Badge variant="secondary" className={credit.needsApproval ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700'}>
          {credit.needsApproval ? 'New bills need credit approval to post' : 'Within credit'}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {cell('Credit limit', credit.limit ? formatCurrency(credit.limit) : 'Not set')}
        {cell('Credit days', credit.creditDays ? `${credit.creditDays} days` : 'Not set')}
        {cell('Outstanding', formatCurrency(credit.outstanding))}
        {cell('Available', credit.available === null ? '—' : formatCurrency(credit.available), credit.available !== null && credit.available < 0 ? 'text-red-600' : '')}
        {cell('Overdue', credit.overdueAmount ? `${formatCurrency(credit.overdueAmount)} · ${credit.overdueCount}` : '—', credit.overdueAmount ? 'text-red-600' : '')}
        {cell('Advances received', formatCurrency(credit.advancesReceived))}
      </div>
      {!!credit.oldDues && <p className="mt-3 text-xs text-muted-foreground">Outstanding includes {formatCurrency(credit.oldDues)} old dues from before the CRM.</p>}
      {credit.message && <p className="mt-2 text-xs font-medium text-amber-800 dark:text-amber-300">{credit.message}</p>}
    </div>
  );
}

function AgingCell({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <span className={`w-2 h-2 rounded-full ${color}`} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-sm font-semibold">{formatCurrency(value)}</p>
    </div>
  );
}

function SamplesComplaintsTab({ data, onViewSample }: { data: Client360Response; onViewSample: (id: string) => void }) {
  const { samples, complaints } = data.documents;

  return (
    <div className="space-y-8">
      <div>
        <SectionHeader title="Sample Requests" count={samples.length} />
        {samples.length === 0 ? (
          <EmptyState label="No sample requests." icon={FlaskConical} />
        ) : (
          <DataTable headers={['Code', 'Date', 'Status', 'Products', 'View']}>
            {samples.map((s) => (
              <tr key={s._id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5 font-mono text-xs font-medium">{s.code || '—'}</td>
                <td className="px-4 py-2.5 text-muted-foreground text-xs">{formatDate(s.createdAt)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={s.status} /></td>
                <td className="px-4 py-2.5 text-xs max-w-xs truncate">
                  {s.lines?.map(l => `${l.productName} (${l.packSize})`).join(', ') || '—'}
                </td>
                <td className="px-4 py-2.5"><Button size="sm" variant="outline" onClick={() => onViewSample(s._id)}>View</Button></td>
              </tr>
            ))}
          </DataTable>
        )}
      </div>

      <div>
        <SectionHeader title="Complaints & Service Tickets" count={complaints.length} />
        {complaints.length === 0 ? (
          <EmptyState label="No complaints." icon={ShieldAlert} />
        ) : (
          <DataTable headers={['Ticket', 'Title', 'Date', 'Status', 'Priority']}>
            {complaints.map((c) => (
              <tr key={c._id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5 font-mono text-xs font-medium">{c.ticketNumber || c.cmsNo || '—'}</td>
                <td className="px-4 py-2.5 text-sm max-w-xs truncate">{c.title || '—'}</td>
                <td className="px-4 py-2.5 text-muted-foreground text-xs">{formatDate(c.createdAt)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={c.cmsStatus || c.status} /></td>
                <td className="px-4 py-2.5"><PriorityBadge priority={c.priority} /></td>
              </tr>
            ))}
          </DataTable>
        )}
      </div>
    </div>
  );
}

function CommunicationsTab({ data }: { data: Client360Response }) {
  const { calls, meetings, followups } = data.activity;
  const [filterType, setFilterType] = useState<string>('all');

  const timeline = useMemo(() => {
    const items = [
      ...calls.map(c => ({ id: c._id, type: 'Call' as const, date: c.created_at, detail: c.notes || c.disposition || '', meta: c.duration_sec ? `${Math.ceil(c.duration_sec / 60)} min` : '' })),
      ...meetings.map(m => ({ id: m._id, type: 'Meeting' as const, date: m.scheduled_at, detail: m.title, meta: m.status || '' })),
      ...followups.map(f => ({ id: f._id, type: 'Follow-up' as const, date: f.follow_up_date || f.created_at, detail: f.notes || '', meta: '' })),
    ].sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
    if (filterType === 'all') return items;
    return items.filter(i => i.type === filterType);
  }, [calls, meetings, followups, filterType]);

  if (calls.length === 0 && meetings.length === 0 && followups.length === 0) {
    return <EmptyState label="No communication history." icon={MessageSquare} />;
  }

  const typeColors: Record<string, string> = { Call: 'bg-blue-500', Meeting: 'bg-green-500', 'Follow-up': 'bg-amber-500' };

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-muted-foreground" />
        {['all', 'Call', 'Meeting', 'Follow-up'].map((t) => (
          <button
            key={t}
            onClick={() => setFilterType(t)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              filterType === t ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'all' ? `All (${calls.length + meetings.length + followups.length})` : t}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="space-y-1">
        {timeline.map((item) => (
          <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/30 transition-colors">
            <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${typeColors[item.type] || 'bg-gray-400'}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{item.type}</Badge>
                {item.meta && <span className="text-xs text-muted-foreground">{item.meta}</span>}
              </div>
              <p className="text-sm mt-0.5 truncate">{item.detail || 'No details recorded'}</p>
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">{formatRelativeDate(item.date)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FinancialsTab({ data }: { data: Client360Response }) {
  const { financials, channelInfo, loyalty, vouchers } = data;

  return (
    <div className="space-y-6">
      {/* Credit & Revenue Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Credit Limit" value={formatCurrency(financials.creditLimit)} icon={IndianRupee} color="primary" />
        <MetricCard label="Outstanding" value={formatCurrency(financials.outstanding)} icon={financials.outstanding > 0 ? TrendingDown : CheckCircle2} color={financials.outstanding > 0 ? 'amber' : 'green'} />
        <MetricCard label="Lifetime Revenue" value={formatCurrency(financials.lifetimeRevenue)} icon={TrendingUp} color="green" />
        <MetricCard label="Lifetime Orders" value={String(financials.lifetimeOrders)} icon={Package} color="purple" />
      </div>

      {/* Channel & Pricing */}
      <div className="rounded-xl border border-border bg-card p-5">
        <SectionHeader title="Channel & Pricing Configuration" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-border/50 p-4 bg-muted/20">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Category</p>
            <p className="font-medium">{CHANNEL_LABELS[channelInfo.channelCategory] || channelInfo.channelCategory}</p>
          </div>
          <div className="rounded-lg border border-border/50 p-4 bg-muted/20">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Price Tier</p>
            <p className="font-medium">{channelInfo.priceTier}</p>
          </div>
          <div className="rounded-lg border border-border/50 p-4 bg-muted/20">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Upstream Chain</p>
            <p className="font-medium">{channelInfo.upstreamChain.length > 0 ? channelInfo.upstreamChain.map(c => c.role).join(' → ') : 'None (Direct)'}</p>
          </div>
        </div>
      </div>

      {/* Loyalty */}
      {loyalty.totalEarned > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <SectionHeader title="Loyalty Program" />
          <div className="grid grid-cols-3 gap-4 mb-4">
            <MetricCard label="Balance" value={loyalty.balance.toLocaleString()} icon={Star} color="purple" />
            <MetricCard label="Total Earned" value={loyalty.totalEarned.toLocaleString()} color="green" />
            <MetricCard label="Total Redeemed" value={loyalty.totalRedeemed.toLocaleString()} color="amber" />
          </div>
          {loyalty.transactions.length > 0 && (
            <DataTable headers={['Type', 'Points', 'Date', 'Reference']}>
              {loyalty.transactions.slice(0, 10).map((t) => (
                <tr key={t._id} className="hover:bg-muted/30">
                  <td className="px-4 py-2"><Badge variant={t.type === 'earned' ? 'secondary' : 'outline'} className="text-xs capitalize">{t.type}</Badge></td>
                  <td className={`px-4 py-2 font-mono text-sm font-medium ${t.points > 0 ? 'text-green-600' : 'text-red-600'}`}>{t.points > 0 ? '+' : ''}{t.points}</td>
                  <td className="px-4 py-2 text-muted-foreground text-xs">{formatDate(t.createdAt)}</td>
                  <td className="px-4 py-2 text-xs">{t.narration || t.referenceNumber || '—'}</td>
                </tr>
              ))}
            </DataTable>
          )}
        </div>
      )}

      {/* Voucher Redemptions */}
      {vouchers.redemptions.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <SectionHeader title="Voucher Redemptions" count={vouchers.redemptions.length} />
          <DataTable headers={['Code', 'Date', 'Document', '_right:Discount']}>
            {vouchers.redemptions.map((v) => (
              <tr key={v._id} className="hover:bg-muted/30">
                <td className="px-4 py-2 font-mono text-xs font-medium">{v.voucherCode}</td>
                <td className="px-4 py-2 text-muted-foreground text-xs">{formatDate(v.redeemedAt)}</td>
                <td className="px-4 py-2 text-xs">{v.documentNumber || '—'}</td>
                <td className="px-4 py-2 text-right font-mono text-sm text-green-600">{formatMinor(v.discountAppliedMinor)}</td>
              </tr>
            ))}
          </DataTable>
        </div>
      )}
    </div>
  );
}

/**
 * Every commercial document raised for this client, in the order the paperwork
 * actually happens: quotation -> proforma -> tax invoice -> receipt.
 *
 * This was a placeholder reading "documents will appear here once document
 * storage is enabled" — but the documents were never missing, only unqueried:
 * the profile read the empty V2 engine instead of the live pipeline. The
 * client's own quotations and proformas sat in SalesOrderModel the whole time.
 */
function DocumentsTab({ data }: { data: Client360Response }) {
  const { customerPurchaseOrders = [], dispatchAttachments = [], quotations, proformas, invoices, payments } = data.documents;
  const nothing = dispatchAttachments.length === 0 && customerPurchaseOrders.length === 0 && quotations.length === 0
    && proformas.length === 0 && invoices.length === 0 && payments.length === 0;

  if (nothing) return <EmptyState label="No documents raised for this client yet." />;

  return (
    <div className="space-y-6">
      {dispatchAttachments.length > 0 && (
        <section>
          <SectionHeader title="Sales hand-off attachments" count={dispatchAttachments.length} />
          <DataTable headers={['Order', 'Type', 'File']}>
            {dispatchAttachments.map((file, index) => (
              <tr key={`${file.orderId}-${file.kind}-${index}`}>
                <td className="px-4 py-2.5">{file.orderNumber}</td>
                <td className="px-4 py-2.5">{file.kind}</td>
                <td className="px-4 py-2.5"><AttachmentPreviewButton url={file.url} name={file.name} /></td>
              </tr>
            ))}
          </DataTable>
        </section>
      )}
      {/* The customer's OWN paperwork, first: it is what authorises everything
          below it, and it is the document somebody hunts for in a dispute. */}
      {customerPurchaseOrders.length > 0 && (
        <section>
          <SectionHeader title="Customer Purchase Orders" count={customerPurchaseOrders.length} />
          <DataTable headers={['PO Number', 'PO Date', 'Received on', 'Document']}>
            {customerPurchaseOrders.map((po) => (
              <tr key={po._id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5 font-mono text-xs font-medium">{po.poNumber || '—'}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{formatDate(po.poDate)}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{po.againstNumber || '—'}</td>
                <td className="px-4 py-2.5 text-xs">
                  {po.attachments.length === 0 ? (
                    <span className="text-muted-foreground">No file attached</span>
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      {po.attachments.map((a) => (
                        <a key={a.url} href={a.url} target="_blank" rel="noopener noreferrer" className="truncate text-primary hover:underline">
                          {a.name}
                        </a>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </DataTable>
        </section>
      )}

      {quotations.length > 0 && (
        <section>
          <SectionHeader title="Quotations" count={quotations.length} />
          <DataTable headers={['Number', 'Date', 'Status', 'Value', 'View']}>
            {quotations.map((q) => (
              <tr key={q._id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5 font-mono text-xs font-medium">
                  {q.orderNumber || q.quotationNo || q.documentNumber || '—'}
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{formatDate(q.date || q.createdAt)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={q.status} /></td>
                <td className="px-4 py-2.5 text-sm font-medium">
                  {formatCurrency(q.grandTotal ?? (q.grandTotalMinor || 0) / 100)}
                </td>
                <td className="px-4 py-2.5"><ViewDocumentButton docType="Quotation" id={q._id} source={q.client360Source} /></td>
              </tr>
            ))}
          </DataTable>
        </section>
      )}

      {proformas.length > 0 && (
        <section>
          <SectionHeader title="Proforma Invoices" count={proformas.length} />
          <DataTable headers={['Number', 'Date', 'Status', 'Value', 'View']}>
            {proformas.map((pf) => (
              <tr key={pf._id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5 font-mono text-xs font-medium">
                  {pf.orderNumber || pf.documentNumber || '—'}
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{formatDate(pf.date || pf.createdAt)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={pf.status} /></td>
                <td className="px-4 py-2.5 text-sm font-medium">
                  {formatCurrency(pf.grandTotal ?? (pf.grandTotalMinor || 0) / 100)}
                </td>
                <td className="px-4 py-2.5"><ViewDocumentButton docType="Proforma" id={pf._id} source={pf.client360Source} /></td>
              </tr>
            ))}
          </DataTable>
        </section>
      )}

      {invoices.length > 0 && (
        <section>
          <SectionHeader title="Tax Invoices" count={invoices.length} />
          <DataTable headers={['Number', 'Date', 'Status', 'Total', 'Due', 'View']}>
            {invoices.map((inv) => (
              <tr key={inv._id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5 font-mono text-xs font-medium">
                  {inv.invoiceNumber || inv.invoiceNo || '—'}
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{formatDate(inv.date || inv.createdAt)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={inv.status} /></td>
                <td className="px-4 py-2.5 text-sm font-medium">
                  {formatCurrency(inv.grandTotal ?? (inv.grandTotalMinor || 0) / 100)}
                </td>
                <td className="px-4 py-2.5 text-sm">
                  {(inv.amountDue || 0) > 0
                    ? <span className="font-semibold text-rose-600">{formatCurrency(inv.amountDue || 0)}</span>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-2.5"><ViewDocumentButton docType="Invoice" id={inv._id} source={inv.client360Source} /></td>
              </tr>
            ))}
          </DataTable>
        </section>
      )}

      {payments.length > 0 && (
        <section>
          <SectionHeader title="Payment Receipts" count={payments.length} />
          <DataTable headers={['Number', 'Date', 'Method', 'Reference', 'Amount', 'Proof']}>
            {payments.map((pay) => (
              <tr key={pay._id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5 font-mono text-xs font-medium">{pay.paymentNumber || '—'}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{formatDate(pay.date || pay.createdAt)}</td>
                <td className="px-4 py-2.5 text-sm capitalize">{(pay.paymentMethod || '—').replaceAll('_', ' ')}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{pay.referenceNumber || '—'}</td>
                <td className="px-4 py-2.5 text-sm font-medium">{formatCurrency(pay.amount || 0)}</td>
                {/* The receipt or screenshot the customer sent. A reference
                    number is a claim; this is the evidence behind it. */}
                <td className="px-4 py-2.5 text-xs">
                  {(pay.attachments?.length || 0) === 0 ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      {pay.attachments!.map((a) => (
                        <a
                          key={a.fileUrl}
                          href={a.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate text-primary hover:underline"
                        >
                          {a.originalName || 'Attachment'}
                        </a>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </DataTable>
        </section>
      )}
    </div>
  );
}

// ── Small utility components ─────────────────────────────────────────────────

function StatusBadge({ status }: { status?: string | null }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  const colorMap: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    confirmed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    posted: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    delivered: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    closed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    converted: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    partially_paid: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    voided: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };
  const baseClass = colorMap[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${baseClass}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function PriorityBadge({ priority }: { priority?: string | null }) {
  if (!priority) return <span className="text-xs text-muted-foreground">—</span>;
  const colorMap: Record<string, string> = {
    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    normal: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  };
  const baseClass = colorMap[priority] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${baseClass}`}>
      {priority}
    </span>
  );
}

function EmptyState({ label, icon: Icon = FolderOpen }: { label: string; icon?: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground rounded-xl border border-dashed border-border bg-muted/10">
      <Icon className="w-10 h-10 mb-3 opacity-30" />
      <p className="text-sm">{label}</p>
    </div>
  );
}
